import React from 'react';
import { Form, Input, Button, Tooltip } from 'antd';
import { DeleteFilled, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  customAppDomainValidator,
  emailDomainValidator,
  certificateValidator,
  trimCertificate,
  applicationDomainValidator,
} from 'utils/inputValidators/inputValidators';
import {
  domainIsDuplicate,
  getInputStatus,
} from 'pages/Home/NewProfileForm/DomainsInput/validators.ts';
import { isFirstProfileInput, isLastProfileInput } from '../utils';
import InputLabel from './InputLabel/InputLabel';
import { fillTemplate, hasTemplate, matchesTemplate, templateMarker } from './templateUtils.ts';
import './styles.less';

class ProfileInputs extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      azDomainsValidation: [],
    };
  }

  componentDidMount() {
    const { form, errors } = this.props;

    if (errors) {
      const inputs = Object.keys(errors);
      form.current.validateFields(inputs, { force: true });
    }

    this.updateAzDomainsValidation();
  }

  componentDidUpdate(prevProps) {
    const { form, errors } = this.props;

    if (errors && prevProps.errors !== errors) {
      const inputs = Object.keys(errors);
      form.current.validateFields(inputs, { force: true });
    }
  }

  updateAzDomainsValidation = () => {
    const { form } = this.props;
    const values = form.current?.getFieldValue('azDomains');
    const tempStatus = [];
    if (values) {
      values.forEach((v, i) => {
        const status = getInputStatus(
          v,
          values.filter((_, index) => index !== i),
        );
        tempStatus.push(status);
      });
    }
    this.setState({ azDomainsValidation: tempStatus });
  };

  render() {
    const {
      hideCustomAppDomain,
      hideAzDomains,
      editableFields,
      form,
      values: {
        azDomains,
        customAppDomain,
        issuer,
        openIdClientId,
        acsUrl,
        emailDomain,
        clientSecret,
        mandatoryCertificate,
        optionalCertificate,
      },
      errors,
      onChange,
      onSubmit,
      t,
    } = this.props;
    const { azDomainsValidation } = this.state;

    let azDomainsInput = null;
    let customAppDomainInput = null;
    let issuerInput = null;
    let openIdClientIdInput = null;
    let redirectUrisInput = null;
    let emailDomainInput = null;
    let clientSecretInput = null;
    let mandatoryCertificateInput = null;
    let optionalCertificateInput = null;

    const serverErrorValidator = (inputId) =>
      new Promise((resolve, reject) => {
        if (errors[inputId]) {
          reject(errors[inputId]);
        } else {
          resolve();
        }
      });

    const handleSubmit = (e) => {
      e?.preventDefault();
      onSubmit();
    };

    const initialValues = {
      azDomains: azDomains || editableFields.azDomains,
      issuer: issuer || editableFields.issuer,
      openIdClientId: openIdClientId || editableFields.openIdClientId,
      acsUrl: acsUrl || editableFields.acsUrl,
      emailDomain: emailDomain || editableFields.emailDomain,
      clientSecret: clientSecret || editableFields.clientSecret,
      mandatoryCertificate: mandatoryCertificate || editableFields.mandatoryCertificate,
      optionalCertificate: optionalCertificate || editableFields.optionalCertificate,
    };

    if (!hideAzDomains && Object.prototype.hasOwnProperty.call(editableFields, 'azDomains')) {
      azDomainsInput = (
        <>
          <div className="profile-inputs__az-domains-label">
            <InputLabel
              title={t('profileInputs.azDomains.title')}
              subtitle={t('profileInputs.azDomains.subtitle')}
            />
          </div>
          <Form.List name="azDomains">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => {
                  const inputStatus = azDomainsValidation[index];
                  const asyncValidator = () =>
                    new Promise((resolve, reject) => {
                      const values = form.current?.getFieldValue('azDomains');
                      if (values) {
                        const currentValue = values[index];
                        const otherValues = values.filter((_, i) => i !== index);
                        const isDuplicate = domainIsDuplicate(currentValue, otherValues);

                        if (isDuplicate) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          reject(t('profileInputs.azDomains.duplicateError'));
                        }

                        resolve(true);
                      }
                    });

                  return (
                    <Form.Item
                      help={inputStatus?.message}
                      validateStatus={inputStatus?.status}
                      label={`Domain ${index + 1}`}
                      key={field.key}
                      rules={[
                        {
                          validator: () => serverErrorValidator('azDomains'),
                        },
                      ]}
                    >
                      <Form.Item
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            required: true,
                            message: t('profileInputs.customAppDomain.requiredError'),
                          },
                          {
                            validator: applicationDomainValidator,
                          },
                          {
                            validator: asyncValidator,
                          },
                        ]}
                        shouldUpdate
                        noStyle
                        name={field.name}
                      >
                        <Input
                          onChange={() => {
                            onChange('azDomains');
                            this.updateAzDomainsValidation();
                          }}
                          className="profile-inputs__input"
                          prefix={
                            inputStatus?.showWarning ? (
                              <Tooltip title={inputStatus?.warningTooltip}>
                                <WarningOutlined style={{ color: '#FAAD14' }} />
                              </Tooltip>
                            ) : (
                              <span />
                            )
                          }
                        />
                      </Form.Item>
                      {index !== 0 && (
                        <Button
                          onClick={() => {
                            remove(field.name);
                            onChange('azDomains');
                            this.updateAzDomainsValidation();
                          }}
                          icon={<DeleteFilled style={{ color: '#BFBFBF' }} />}
                          className="profile-inputs__az-domain-delete-button"
                          type="text"
                        />
                      )}
                    </Form.Item>
                  );
                })}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => {
                      add();
                      onChange('azDomains');
                      this.updateAzDomainsValidation();
                    }}
                    icon={<PlusOutlined />}
                    className="profile-inputs__input"
                  >
                    {t('profileInputs.azDomains.addButton')}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </>
      );
    }

    if (
      !hideCustomAppDomain &&
      Object.prototype.hasOwnProperty.call(editableFields, 'customAppDomain')
    ) {
      initialValues.customAppDomain = customAppDomain || editableFields.customAppDomain;
      const appDomainTemplate = editableFields.customAppDomain.split(templateMarker);
      customAppDomainInput = (
        <Form.Item
          name="customAppDomain"
          className="profile-inputs__form-item"
          rules={[
            { required: true, message: t('profileInputs.customAppDomain.requiredError') },
            { validator: customAppDomainValidator },
            {
              validator: () => serverErrorValidator('customAppDomain'),
            },
          ]}
          label={
            <InputLabel
              title={t('profileInputs.customAppDomain.title')}
              subtitle={t('profileInputs.customAppDomain.subtitle')}
            />
          }
        >
          <Input
            addonBefore={appDomainTemplate[0]}
            addonAfter={appDomainTemplate[1]}
            type="text"
            placeholder={t('profileInputs.customAppDomain.placeholder')}
            className="profile-inputs__input"
            onChange={() => {
              onChange('customAppDomain');
            }}
            autoFocus={isFirstProfileInput(editableFields, 'customAppDomain', false, hideAzDomains)}
            onPressEnter={
              isLastProfileInput(editableFields, 'customAppDomain') ? handleSubmit : undefined
            }
          />
        </Form.Item>
      );
    }

    if (Object.prototype.hasOwnProperty.call(editableFields, 'issuer')) {
      issuerInput = (
        <Form.Item
          name="issuer"
          className="profile-inputs__form-item"
          rules={[
            { required: true, message: t('profileInputs.clientID.requiredError') },
            {
              validator: () => serverErrorValidator('issuer'),
            },
          ]}
          label={
            <InputLabel
              title={t('profileInputs.clientID.title')}
              subtitle={t('profileInputs.clientID.subtitle')}
            />
          }
        >
          <Input
            type="text"
            placeholder={t('profileInputs.clientID.placeholder')}
            className="profile-inputs__input"
            onChange={() => {
              onChange('issuer');
            }}
            autoFocus={isFirstProfileInput(
              editableFields,
              'issuer',
              hideCustomAppDomain,
              hideAzDomains,
            )}
            onPressEnter={isLastProfileInput(editableFields, 'issuer') ? handleSubmit : undefined}
          />
        </Form.Item>
      );
    }

    if (Object.prototype.hasOwnProperty.call(editableFields, 'openIdClientId')) {
      openIdClientIdInput = (
        <Form.Item
          name="openIdClientId"
          className="profile-inputs__form-item"
          rules={[
            { required: true, message: t('profileInputs.openIdClientId.requiredError') },
            {
              validator: () => serverErrorValidator('openIdClientId'),
            },
          ]}
          label={
            <InputLabel
              title={t('profileInputs.openIdClientId.title')}
              subtitle={t('profileInputs.openIdClientId.subtitle')}
            />
          }
        >
          <Input
            type="text"
            placeholder={t('profileInputs.openIdClientId.placeholder')}
            className="profile-inputs__input"
            onChange={() => {
              onChange('openIdClientId');
            }}
            autoFocus={isFirstProfileInput(
              editableFields,
              'openIdClientId',
              hideCustomAppDomain,
              hideAzDomains,
            )}
            onPressEnter={
              isLastProfileInput(editableFields, 'openIdClientId') ? handleSubmit : undefined
            }
          />
        </Form.Item>
      );
    }

    if (Object.prototype.hasOwnProperty.call(editableFields, 'acsUrl')) {
      redirectUrisInput = (
        <Form.Item
          name="acsUrl"
          className="profile-inputs__form-item"
          rules={[
            { required: true, message: t('profileInputs.redirectURI.requiredError') },
            { type: 'url', message: t('profileInputs.redirectURI.urlError') },
            {
              validator: () => serverErrorValidator('acsUrl'),
            },
          ]}
          label={
            <InputLabel
              title={t('profileInputs.redirectURI.title')}
              subtitle={t('profileInputs.redirectURI.subtitle')}
            />
          }
        >
          <Input
            type="text"
            placeholder={t('profileInputs.redirectURI.placeholder')}
            className="profile-inputs__input"
            onChange={() => {
              onChange('acsUrl');
            }}
            autoFocus={isFirstProfileInput(
              editableFields,
              'acsUrl',
              hideCustomAppDomain,
              hideAzDomains,
            )}
            onPressEnter={isLastProfileInput(editableFields, 'acsUrl') ? handleSubmit : undefined}
          />
        </Form.Item>
      );
    }

    if (Object.prototype.hasOwnProperty.call(editableFields, 'emailDomain')) {
      const fillEmailTemplates = (newEmailDomain) => {
        const { issuer: currentIssuer, acsUrl: currentAcs } = form.current.getFieldsValue([
          'issuer',
          'acsUrl',
        ]);
        const newIssuer =
          hasTemplate(editableFields.issuer) &&
          matchesTemplate(currentIssuer, editableFields.issuer)
            ? fillTemplate(newEmailDomain, editableFields.issuer)
            : undefined;

        const newAcs =
          hasTemplate(editableFields.acsUrl) && matchesTemplate(currentAcs, editableFields.acsUrl)
            ? fillTemplate(newEmailDomain, editableFields.acsUrl)
            : undefined;

        if (newIssuer || newAcs) {
          const newValues = {};
          if (newIssuer) {
            newValues.issuer = newIssuer;
          }

          if (newAcs) {
            newValues.acsUrl = newAcs;
          }

          form.current.setFieldsValue(newValues);
        }
      };

      emailDomainInput = (
        <Form.Item
          name="emailDomain"
          className="profile-inputs__form-item"
          rules={[
            { required: true, message: t('profileInputs.emailDomain.requiredError') },
            {
              validator: emailDomainValidator,
            },
            {
              validator: () => serverErrorValidator('emailDomain'),
            },
          ]}
          label={
            <InputLabel
              title={t('profileInputs.emailDomain.title')}
              subtitle={t('profileInputs.emailDomain.subtitle')}
            />
          }
        >
          <Input
            type="text"
            placeholder={t('profileInputs.emailDomain.placeholder')}
            className="profile-inputs__input"
            onChange={(event) => {
              onChange('emailDomain');
              fillEmailTemplates(event.target.value);
            }}
            addonBefore="@"
            autoFocus={isFirstProfileInput(
              editableFields,
              'emailDomain',
              hideCustomAppDomain,
              hideAzDomains,
            )}
            onPressEnter={
              isLastProfileInput(editableFields, 'emailDomain') ? handleSubmit : undefined
            }
          />
        </Form.Item>
      );
    }

    if (Object.prototype.hasOwnProperty.call(editableFields, 'clientSecret')) {
      clientSecretInput = (
        <Form.Item
          name="clientSecret"
          className="profile-inputs__form-item"
          rules={[
            { required: true, message: t('profileInputs.clientSecret.requiredError') },
            {
              validator: () => serverErrorValidator('clientSecret'),
            },
          ]}
          label={
            <InputLabel
              title={t('profileInputs.clientSecret.title')}
              subtitle={t('profileInputs.clientSecret.subtitle')}
            />
          }
        >
          <Input
            type="text"
            placeholder={t('profileInputs.clientSecret.placeholder')}
            className="profile-inputs__input"
            onChange={() => {
              onChange('clientSecret');
            }}
            autoFocus={isFirstProfileInput(
              editableFields,
              'clientSecret',
              hideCustomAppDomain,
              hideAzDomains,
            )}
            onPressEnter={
              isLastProfileInput(editableFields, 'clientSecret') ? handleSubmit : undefined
            }
          />
        </Form.Item>
      );
    }

    if (Object.prototype.hasOwnProperty.call(editableFields, 'mandatoryCertificate')) {
      mandatoryCertificateInput = (
        <Form.Item
          name="mandatoryCertificate"
          className="profile-inputs__form-item"
          rules={[
            { required: true, message: t('profileInputs.certificate.requiredError') },
            { transform: trimCertificate },
            { validator: certificateValidator },
            {
              validator: () => serverErrorValidator('mandatoryCertificate'),
            },
          ]}
          label={
            <InputLabel
              title={t('profileInputs.certificate.title')}
              subtitle={t('profileInputs.certificate.subtitle')}
            />
          }
        >
          <Input.TextArea
            placeholder={t('profileInputs.certificate.placeholder')}
            className="profile-inputs__input-text-area"
            spellCheck={false}
            onChange={() => {
              onChange('mandatoryCertificate');
            }}
            autoFocus={isFirstProfileInput(
              editableFields,
              'mandatoryCertificate',
              hideCustomAppDomain,
              hideAzDomains,
            )}
          />
        </Form.Item>
      );
    }
    if (Object.prototype.hasOwnProperty.call(editableFields, 'optionalCertificate')) {
      optionalCertificateInput = (
        <Form.Item
          name="optionalCertificate"
          className="profile-inputs__form-item"
          rules={[
            { transform: trimCertificate },
            { validator: certificateValidator },
            {
              validator: () => serverErrorValidator('optionalCertificate'),
            },
          ]}
          label={
            <InputLabel
              title={t('profileInputs.optionalCertificate.title')}
              subtitle={t('profileInputs.certificate.subtitle')}
            />
          }
        >
          <Input.TextArea
            placeholder={t('profileInputs.certificate.placeholder')}
            className="profile-inputs__input-text-area"
            spellCheck={false}
            onChange={() => {
              onChange('optionalCertificate');
            }}
            autoFocus={isFirstProfileInput(
              editableFields,
              'optionalCertificate',
              hideCustomAppDomain,
              hideAzDomains,
            )}
          />
        </Form.Item>
      );
    }

    return (
      <Form
        name="profileInputsForm"
        initialValues={initialValues}
        ref={form}
        layout="vertical"
        requiredMark={false}
      >
        {azDomainsInput}
        {customAppDomainInput}
        {emailDomainInput}
        {issuerInput}
        {openIdClientIdInput}
        {redirectUrisInput}
        {clientSecretInput}
        {mandatoryCertificateInput}
        {optionalCertificateInput}
      </Form>
    );
  }
}

ProfileInputs.propTypes = {
  hideCustomAppDomain: PropTypes.bool,
  hideAzDomains: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  editableFields: PropTypes.object.isRequired,
  values: PropTypes.shape({
    azDomains: PropTypes.arrayOf(PropTypes.string),
    customAppDomain: PropTypes.string,
    issuer: PropTypes.string,
    openIdClientId: PropTypes.string,
    acsUrl: PropTypes.string,
    emailDomain: PropTypes.string,
    clientSecret: PropTypes.string,
    mandatoryCertificate: PropTypes.string,
    optionalCertificate: PropTypes.string,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.object,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  form: PropTypes.shape({
    current: PropTypes.shape({
      validateFields: PropTypes.func.isRequired,
      setFieldsValue: PropTypes.func.isRequired,
      isFieldTouched: PropTypes.func.isRequired,
      getFieldError: PropTypes.func.isRequired,
      getFieldsValue: PropTypes.func.isRequired,
      getFieldValue: PropTypes.func.isRequired,
    }),
  }),
  t: PropTypes.func.isRequired,
};

ProfileInputs.defaultProps = {
  hideCustomAppDomain: false,
  hideAzDomains: false,
  values: {
    azDomains: [''],
    customAppDomain: '',
    issuer: '',
    openIdClientId: '',
    acsUrl: '',
    emailDomain: '',
    clientSecret: '',
    mandatoryCertificate: '',
    optionalCertificate: '',
  },
  form: React.createRef(),
  errors: {},
  onChange: () => {},
  onSubmit: () => {},
};

export default withTranslation()(ProfileInputs);
